import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { forgotPassword, resendVerificationEmail } from '@/controller/auth';
import { toast } from '@/shared/components/UI';

const VerificationPage = () => {
    const [type, setType] = useState('---');
    const [email, setEmail] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const [load, setLoad] = useState(false);

    const nav = useNavigate();

    useEffect(() => {
        const em = searchParams.get('email');
        const type = searchParams.get('type');

        setType(type);

        if (em === null || em === '') {
            nav('/');
        } else {
            setEmail(em);
            searchParams.delete('email');
            searchParams.delete('type');
            setSearchParams(searchParams);
        }
    }, []);

    const resend = async () => {
        setLoad(true);
        try {
            const ret = await resendVerificationEmail(email);
            if (ret.status === 200) {
                toast.success('Sent successfully. Please proceed to your email to verify account.');
                setLoad(false);
            } else {
                toast.error(ret.data.message);
                setLoad(false);
            }
        } catch (e) {}
    };

    const forgotPw = async () => {
        setLoad(true);
        try {
            const ret = await forgotPassword(email);
            if (ret.status === 200) {
                nav('/forgot-password?email=' + email + '&type=done-reset');
            } else {
                toast.error(ret.data.message);
                setLoad(false);
            }
        } catch (e) {}
    };

    if (type === 'signup-success') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4 ">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Verify your email
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    We've sent an email to <span className="text-[#111218] font-medium">{email}</span>{' '}
                                    to verify your email address and activate your account.
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                    The link in the email will expire in 24 hours
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="px-4 py-6 text-center justify-center">
                            <p className="text-[#5f678c] text-sm font-normal leading-normal mb-4">
                                Didn't get the email message?
                            </p>
                            <button
                                className={`min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full`}
                                disabled={load}
                                onClick={resend}
                            >
                                {load ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Sending...</span>
                                    </div>
                                ) : (
                                    'Resend Verification Email'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (type === 'pending-activation') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Your account is pending activation
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    Please click the link we've sent to{' '}
                                    <span className="text-[#111218] font-medium">{email}</span> to verify your email
                                    address and activate your account.
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                    The link in the email will expire in 24 hours
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="px-4 py-6 text-center">
                            <p className="text-[#5f678c] text-sm font-normal leading-normal mb-4">
                                Didn't get the email message?
                            </p>
                            <button
                                className={`min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full`}
                                disabled={load}
                                onClick={resend}
                            >
                                {load ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Sending...</span>
                                    </div>
                                ) : (
                                    'Resend Verification Email'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (type === 'password-resets') {
        return (
            <div className="relative flex size-full min-h-screen flex-col bg-white">
                <div className="px-20 lg:px-40 xl:px-40 flex flex-1 justify-center py-5 font-sans">
                    <div className="flex flex-col w-full max-w-2xl py-5 flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap gap-3 p-4">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Your password reset link has expired
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-4">
                                    Your request to reset your password has expired or the link has already been used.
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                    Please click the button below to get a new reset password link to{' '}
                                    <span className="text-[#111218] font-medium">{email}</span>.
                                </p>
                            </div>
                        </div>

                        {/* Action Section */}
                        <div className="px-4 py-6 text-center">
                            <button
                                className={`min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full`}
                                disabled={load}
                                onClick={forgotPw}
                            >
                                {load ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Processing...</span>
                                    </div>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else {
        if (type !== '---') {
            return (
                <>
                    <Navigate to="/" />
                </>
            );
        }
    }
};

export default VerificationPage;
